/* eslint-disable @typescript-eslint/ban-ts-comment */
import EventEmitter from '@remote/utils/event-emitter'
import { pathJoin } from '@remote/utils/file-helper'
import HttpUploadFile from './http-upload'
import OSSUploader from './aliyunoss-upload'
import { ITransfeTaskItem } from '@remote/type/upload'
import { IFile } from '@remote/type/hezi'
import Events, { Status, chunkSize } from './enum'
import global from '@remote/store/constantStore'
import { getSsoStore } from '@remote/utils/sso'

const logs = function logFactory (...args: unknown[]) {
  console.log('upload:', ...args)
}

// 事件广播
const EVENTNAMES = [
  Events.UploadTaskStart,
  Events.UploadTaskChange,
  Events.UploadTaskError,
  Events.UploadTaskTransitServerProgress,
  Events.UploadTaskDeviceReceiveProgress,
  Events.UploadTaskTransitServerCompleted,
  Events.UploadTaskDeviceReceiveCompleted,
  Events.UploadTaskdelete,
  Events.UploadTaskRun,
  Events.UploadTaskBatchUpdate,
  Events.UploadTaskPause,
  Events.UploadTaskPauseWaitDeviceReceive,
]

export const LARGE_FILE_SIZE = 1024 * 1024 * 100
const MAX_CONCURRENCY = 3
let _instance: UploaderTaskManager | undefined

export type TTask = IFile & { status: ITransfeTaskItem['status']; error: string; ppath: string; TSProgress: number; DRProgress: number }
export class UploaderTaskManager extends EventEmitter {

  private willExecuteTasks: Map<string, ITransfeTaskItem>          // 待执行任务队列
  private executingQueue: Map<string, ITransfeTaskItem>           // 执行中的任务队列
  private completedQueue: Map<string, ITransfeTaskItem>            // 已完成的任务队列（上传完成，上传任务被删除）
  private interruptedQueue: Map<string, ITransfeTaskItem>         // 被中断的任务队列 (暂停，上传错误的任务)

  // private lastIncompletedTasks: ITransfeTaskItem[]

  private uploaderInstancMap: Map<string, OSSUploader | HttpUploadFile> // 任务传输实例
  private batchOpreate: '' | 'pause' | 'restart'           // 批量操作控制开关
  private runTaskLock: boolean                          // 执行一次任务队列开关

  private recordTransitServerProgressMap: { [attachmentId: string]: number }
  private recordDeviceReceiveProgressMap: { [attachmentId: string]: number }
  private idleTaskNums: { [attachmentId: string]: string }
  private recordTransitServerIdleTaskMap: { [attachmentId: string]: string }
  private order: number

  constructor () {
    super(EVENTNAMES)

    this.willExecuteTasks = new Map()
    this.executingQueue = new Map()
    this.completedQueue = new Map()
    this.interruptedQueue = new Map()

    // this.lastIncompletedTasks = []
    this.uploaderInstancMap = new Map()
    this.batchOpreate = ''
    this.runTaskLock = false
    this.recordTransitServerProgressMap = {}
    this.recordDeviceReceiveProgressMap = {}
    this.idleTaskNums = {}
    this.recordTransitServerIdleTaskMap = {}
    this.order = 0

    this.onProgress = this.onProgress.bind(this)
    this.onDeviceReceiveProgress = this.onDeviceReceiveProgress.bind(this)
    this.onUploadTransitServerCompleted = this.onUploadTransitServerCompleted.bind(this)
    this.onDeviceReceiveCompleted = this.onDeviceReceiveCompleted.bind(this)
    this.onError = this.onError.bind(this)
    this.onDelete = this.onDelete.bind(this)
    this.addTask = this.addTask.bind(this)
    this.stopTask = this.stopTask.bind(this)
    this.starTask = this.starTask.bind(this)
  }

  get tasks (): Array<TTask> {
    // 格式化任务展示
    const mapTaskToFile = (status: ITransfeTaskItem['status'] | '', task: ITransfeTaskItem) => ({
      name: task.file.name,
      size: task.file.size,
      md5sum: task.attachmentId,
      path: pathJoin(task.path, task.file.name),
      ppath: task.path,
      type: 'file',
      ext: task.file.name.replace(/.+\./igm, ''),
      status: status || task.status,
      error: task.error,
      order: task.order,
      TSProgress: this.recordTransitServerProgressMap[task.attachmentId] ? (
        Math.min(Math.ceil(this.recordTransitServerProgressMap[task.attachmentId] * 100), 100)
      ) : 0,
      DRProgress: this.recordDeviceReceiveProgressMap[task.attachmentId] ? (
        Math.min(Math.ceil(this.recordDeviceReceiveProgressMap[task.attachmentId] * 100), 100)
      ) : 0,
    })
    // 根据添加顺序排序
    const sortByOrder = (a: ITransfeTaskItem & { order: number }, b: ITransfeTaskItem & { order: number }) => {
      if (a.order > b.order) {
        return -1
      } else if (a.order < b.order) {
        return 1
      } else {
        return 0
      }
    }

    // @ts-ignore
    return [
      ...Array.from(this.executingQueue.values()).map(mapTaskToFile.bind(null, Status.UploadTransmitting)),
      ...Array.from(this.willExecuteTasks.values()).map(mapTaskToFile.bind(null, Status.UploadPending)),
      ...Array.from(this.interruptedQueue.values()).map(mapTaskToFile.bind(null, '')),
      ...Array.from(this.completedQueue.values()).map(mapTaskToFile.bind(null, '')),
    ].sort(sortByOrder)
  }

  clear () {
    this.willExecuteTasks = new Map()
    this.executingQueue = new Map()
    this.completedQueue = new Map()
    this.interruptedQueue = new Map()

    this.uploaderInstancMap = new Map()
    this.trigger(Events.UploadTaskChange, [])
    this.trigger(Events.UploadTaskRun)
  }

  addTask (tasks: ITransfeTaskItem[]) {
    console.log('addTask', tasks)
    logs('add tasks')
    this.batchOpreate = ''
    tasks.slice().forEach(item => {
      item.order = this.order
      this.willExecuteTasks.set(item.attachmentId, item)
      this.order += 1
    })
    this.trigger(Events.UploadTaskChange, this.tasks)
    this.runTask()
  }

  removeTaskCallback (attachmentId: string) {
    this.uploaderInstancMap.get(attachmentId)?.cancel()
    this.uploaderInstancMap.delete(attachmentId)
    delete this.idleTaskNums[attachmentId]
    this.trigger(Events.UploadTaskRun)
  }

  removeTask (attachmentId: string) {
    // 移除进行中的任务
    let currentTask = this.executingQueue.get(attachmentId)
    if (currentTask) {
      logs('removeTask 1')
      this.executingQueue.delete(attachmentId)
      this.runTask()
      this.trigger(Events.UploadTaskdelete, attachmentId)
      currentTask.error = '上传任务已被取消'
      currentTask.status = Status.UploadDeleted
      this.completedQueue.set(attachmentId, currentTask)
      this.removeTaskCallback(attachmentId)
      return
    }

    // 移除任务列表中的任务
    currentTask = this.willExecuteTasks.get(attachmentId)
    if (currentTask) {
      logs('removeTask 2')
      this.willExecuteTasks.delete(attachmentId)
      this.runTask()
      this.trigger(Events.UploadTaskdelete, attachmentId)
      currentTask.error = '上传任务已被取消'
      currentTask.status = Status.UploadDeleted
      this.completedQueue.set(attachmentId, currentTask)
      this.removeTaskCallback(attachmentId)
      return
    }
    // 移除任务等待中的任务
    currentTask = this.interruptedQueue.get(attachmentId)

    if (currentTask) {
      logs('removeTask 3')
      this.interruptedQueue.delete(attachmentId)
      this.runTask()
      this.trigger(Events.UploadTaskdelete, attachmentId)
      currentTask.error = '上传任务已被取消'
      currentTask.status = Status.UploadDeleted
      this.completedQueue.set(attachmentId, currentTask)
      this.removeTaskCallback(attachmentId)
      return
    }
  }

  stopTask (attachmentId: string, skipRunTask?: boolean) {
    // 将暂停的任务压入interruptedQueue队列中
    const currentTask = this.executingQueue.get(attachmentId)
    logs('stopTask start', attachmentId, currentTask, this.executingQueue, this.willExecuteTasks)
    if (currentTask) {
      this.executingQueue.delete(attachmentId)
      currentTask.status = Status.UploadSuspend
      this.interruptedQueue.set(attachmentId, currentTask)
      this.uploaderInstancMap.get(attachmentId)?.pause()

      if (!skipRunTask && this.willExecuteTasks.size > 0) this.runTask()
    } else {
      const currentTask = this.willExecuteTasks.get(attachmentId)
      if (currentTask) {
        this.willExecuteTasks.delete(attachmentId)
        currentTask.status = Status.UploadSuspend
        this.interruptedQueue.set(attachmentId, currentTask)
        // this.uploaderInstancMap.get(attachmentId)?.cancel()
        // if (!skipRunTask) this.runTask()
      }
    }
    delete this.idleTaskNums[attachmentId]
    logs('stopTask end', attachmentId, this.executingQueue.get(attachmentId), this.interruptedQueue.get(attachmentId))
  }

  pauseTaskToWaitDeviceReceive (attachmentId: string) {
    // 将暂停的任务压入interruptedQueue队列中
    const currentTask = this.executingQueue.get(attachmentId)
    logs('pauseTaskToWaitDeviceReceive start', attachmentId, currentTask, this.executingQueue, this.willExecuteTasks)
    if (currentTask) {
      currentTask.status = Status.UploadSuspend
      this.uploaderInstancMap.get(attachmentId)?.pause(true)
    }
    logs('penddingTask end', attachmentId, this.executingQueue.get(attachmentId), this.interruptedQueue.get(attachmentId))
  }

  continueTaskToUploadOSS (attachmentId: string) {
    // 将暂停的任务压入interruptedQueue队列中
    const currentTask = this.executingQueue.get(attachmentId)
    logs('continueTaskToUploadOSS start', attachmentId, currentTask, this.executingQueue, this.willExecuteTasks)
    if (currentTask) {
      currentTask.status = Status.UploadPending
      // @ts-ignore
      this.uploaderInstancMap.get(attachmentId)?.resume()
    }
    logs('penddingTask end', attachmentId, this.executingQueue.get(attachmentId), this.interruptedQueue.get(attachmentId))
  }

  starTask (attachmentId: string, skipRunTask?: boolean) {
    // 将开始的任务压入willExecuteTasks队列中
    const currentTask = this.interruptedQueue.get(attachmentId) || this.executingQueue.get(attachmentId)
    logs('starTask', attachmentId, currentTask, this.interruptedQueue, this.executingQueue)
    if (currentTask && currentTask.status !== Status.UploadPending) {
      this.interruptedQueue.delete(attachmentId)
      currentTask.status = Status.UploadPending
      currentTask.error = ''
      this.willExecuteTasks.set(attachmentId, currentTask)

      if (!skipRunTask) {
        this.batchOpreate = ''
        this.runTask(1)
      }
    }
  }

  batchUpdateTask (opreate: 'pause' | 'restart') {
    this.batchOpreate = opreate
    return new Promise((resolve) => {
      if (opreate === 'pause') {
        // 将执行中的任务压入interruptedQueue中
        Array.from(this.executingQueue.keys()).forEach(attachmentId => this.stopTask(attachmentId, true))
        // 将任务队列中的任务压入interruptedQueue中
        Array.from(this.willExecuteTasks.keys()).forEach(attachmentId => this.stopTask(attachmentId, true))
      } else {
        // 将interruptedQueue任务压入interruptedQueue中
        Array.from(this.interruptedQueue.keys()).forEach(attachmentId => this.starTask(attachmentId, true))
        this.runTask()
      }
      this.trigger(Events.UploadTaskBatchUpdate, opreate)
      setTimeout(() => {
        resolve(null)
      }, 2000)
    })
  }

  private getIdleNums () {
    let idleNumber = 0
    let unIdleNumber = 0

    const keys = Array.from(this.executingQueue.keys())
    const len = keys.length
    if (!len) {
      return MAX_CONCURRENCY
    } else if (len < MAX_CONCURRENCY) {
      idleNumber = MAX_CONCURRENCY - len
    }

    keys.forEach(key => {
      // 空闲的任务
      if (this.recordTransitServerIdleTaskMap[key]) {
        idleNumber += 1
      } else {
        // 非空闲的任务
        unIdleNumber += 1
      }
    })

    return Math.min(idleNumber, MAX_CONCURRENCY - unIdleNumber)
  }
  private runTask (willExecuteTaskIndex?: number) {
    if (this.runTaskLock) return
    if (this.batchOpreate === 'pause') return

    this.runTaskLock = true
    // const maxConcurrency = MAX_CONCURRENCY + Object.keys(this.idleTaskNums).length
    const maxConcurrency = this.getIdleNums()
    // 空闲任务消费后重新置空
    this.idleTaskNums = {}
    logs('runTask', MAX_CONCURRENCY, this.idleTaskNums, maxConcurrency, this.executingQueue.size)
    console.log('run task:', this.willExecuteTasks.size > 0, this.executingQueue.size < maxConcurrency)
    if (
      this.willExecuteTasks.size > 0 && maxConcurrency > 0
    ) {
      // const allowableExecuteNum = maxConcurrency ? Math.abs(maxConcurrency - this.executingQueue.size) : this.willExecuteTasks.size
      const willExecuteTasksKeys = Array.from(this.willExecuteTasks.keys())
      const allowableExecuteNum = maxConcurrency < this.willExecuteTasks.size ? maxConcurrency : this.willExecuteTasks.size
      const willExecuteTasks = willExecuteTasksKeys.splice(0, willExecuteTaskIndex || allowableExecuteNum)

      for (let i = 0; i < willExecuteTasks.length; i++) {
        const task = this.willExecuteTasks.get(willExecuteTasks[i]) as ITransfeTaskItem
        const { deviceId, path, attachmentId, file } = task
        this.executingQueue.set(attachmentId, task)
        this.willExecuteTasks.delete(attachmentId)

        const uploaderInstanceCache = this.uploaderInstancMap.get(attachmentId)
        const ssoInfo = getSsoStore()
        if (uploaderInstanceCache) {
          uploaderInstanceCache.resume({ file, chunkSize, path, deviceId, token: ssoInfo.access_token, attachmentId })
          logs('runTask 续传', file.name)
        } else {
          const Uploader = file.size > LARGE_FILE_SIZE ? OSSUploader : HttpUploadFile
          const httpUploadFile = new Uploader()
          this.uploaderInstancMap.set(attachmentId, httpUploadFile)
          this.attachEventListener(httpUploadFile)
          httpUploadFile.start({ file, chunkSize, path, deviceId, token: ssoInfo.access_token, attachmentId })
          logs('runTask 上传', file.name)
        }
      }
    }
    // @ts-ignore
    if (this.batchOpreate !== 'pause') this.trigger(Events.UploadTaskRun)
    this.runTaskLock = false
  }

  private attachEventListener (instance: OSSUploader | HttpUploadFile) {
    instance.removeAllEventListener(Events.UploadTaskError)
    instance.removeAllEventListener(Events.UploadTaskTransitServerProgress)
    instance.removeAllEventListener(Events.UploadTaskDeviceReceiveProgress)
    instance.removeAllEventListener(Events.UploadTaskTransitServerCompleted)
    instance.removeAllEventListener(Events.UploadTaskDeviceReceiveCompleted)

    instance.addEventListener(Events.UploadTaskError, this.onError)
    instance.addEventListener(Events.UploadTaskTransitServerProgress, this.onProgress)
    instance.addEventListener(Events.UploadTaskDeviceReceiveProgress, this.onDeviceReceiveProgress)
    instance.addEventListener(Events.UploadTaskTransitServerCompleted, this.onUploadTransitServerCompleted)
    instance.addEventListener(Events.UploadTaskDeviceReceiveCompleted, this.onDeviceReceiveCompleted)
  }

  private onProgress (
    attachmentId: string,
    nCurrent: number,
    nFileSize?: number,
  ) {
    if (this.batchOpreate === 'pause') return
    if (this.executingQueue.has(attachmentId)) {
      this.trigger(Events.UploadTaskTransitServerProgress, attachmentId, nCurrent, nFileSize)
    } else if (this.interruptedQueue.has(attachmentId)) {
      this.trigger(Events.UploadTaskPause, attachmentId, nCurrent, nFileSize)
      // const task = this.interruptedQueue.get(attachmentId)
      // if (task) {
      //     task.status = Status.UploadTransmitting
      //     this.interruptedQueue.delete(attachmentId)
      //     this.executingQueue.set(attachmentId, task)
      //     this.trigger(Events.UploadTaskStart, attachmentId)
      //     this.trigger(Events.UploadTaskTransitServerProgress, attachmentId, nCurrent, nFileSize)
      // }
    }
    this.recordTransitServerProgressMap[attachmentId] = nCurrent
  }

  private onDeviceReceiveProgress (
    attachmentId: string,
    nCurrent: number,
    nFileSize?: number,
  ) {
    if (this.batchOpreate === 'pause') {
      this.trigger(Events.UploadTaskPause, attachmentId, nCurrent, nFileSize)
      return
    }
    if (this.executingQueue.has(attachmentId)) {
      this.trigger(Events.UploadTaskDeviceReceiveProgress, attachmentId, nCurrent, nFileSize)
    }
    this.recordDeviceReceiveProgressMap[attachmentId] = nCurrent
  }

  private onUploadTransitServerCompleted (attachmentId: string, result: string) {
    this.trigger(Events.UploadTaskTransitServerCompleted, attachmentId, result)
    logs('onUploadTransitServerCompleted 闲置任务', attachmentId)
    this.idleTaskNums[attachmentId] = attachmentId
    this.recordTransitServerIdleTaskMap[attachmentId] = attachmentId
    this.recordTransitServerProgressMap[attachmentId] = 1
    this.runTask()
    // const index = this.executingQueue.findIndex(
    //     task => task.attachmentId === attachmentId,
    // )

    // if (index > -1) {
    //     const [currentTask] = this.executingQueue.splice(index, 1)
    //     this.runTask()
    //     this.trigger(Events.UploadTaskTransitServerCompleted, attachmentId, result, currentTask)
    //     currentTask.status = 'completed'
    //     this.completedQueue.push(currentTask)
    // }
  }

  private onDeviceReceiveCompleted (attachmentId: string, result: string) {
    const currentTask = this.executingQueue.get(attachmentId)

    delete this.idleTaskNums[attachmentId]
    delete this.recordTransitServerProgressMap[attachmentId]
    delete this.recordDeviceReceiveProgressMap[attachmentId]
    this.uploaderInstancMap.get(attachmentId)?.destroy()
    this.uploaderInstancMap.delete(attachmentId)

    if (currentTask) {
      this.executingQueue.delete(attachmentId)
      this.runTask()
      this.trigger(Events.UploadTaskDeviceReceiveCompleted, attachmentId, result, currentTask)
      currentTask.status = Status.UploadCompleted
      this.completedQueue.set(attachmentId, currentTask)
    }
  }

  private onError (attachmentId: string, result: string) {
    const currentTask = this.executingQueue.get(attachmentId)
    if (currentTask) {
      this.executingQueue.delete(attachmentId)
      currentTask.error = result
      currentTask.status = Status.UploadFailure

      this.trigger(Events.UploadTaskError, attachmentId, result)
      this.interruptedQueue.set(attachmentId, currentTask)
      // if (this.executingQueue.size < 3) 
      this.runTask()
      logs('------onError-----:', attachmentId, result)
    }
    delete this.idleTaskNums[attachmentId]
  }

  private onDelete (attachmentId: string, result: string) {
    const currentTask = this.executingQueue.get(attachmentId)

    if (currentTask) {
      this.executingQueue.delete(attachmentId)
      this.runTask()
      this.trigger(Events.UploadTaskdelete, attachmentId, result)
      currentTask.status = Status.UploadDeleted
      this.completedQueue.set(attachmentId, currentTask)
    }
    delete this.idleTaskNums[attachmentId]
    delete this.recordTransitServerProgressMap[attachmentId]
    delete this.recordDeviceReceiveProgressMap[attachmentId]
  }
}

export default function getInstance (): UploaderTaskManager | undefined {
  if (!global.fromSpace) return
  if (!_instance) {
    _instance = new UploaderTaskManager()
  }
  window.$UP = _instance
  return _instance
}
